import STableLayout from "@/styles/STableLayout";
import { FC, Fragment, useContext, useMemo, useState } from "react";
import { PackOrderContext } from "../context";
import { Button, Flex, TableProps } from "antd";
import { BoxItem, BoxStatus, PackBox } from "@/types/packing";
import { AnyObject } from "antd/es/_util/type";
import SText from "@/styles/SText";
import { useTranslation } from "react-i18next";
import ItemAddOrEdit from "./ItemAddOrEdit";
import { numFormmater } from "@/pages/Requirement/util";
import { CountrySelect } from "@/components/Selector";
import { orderSourceItems } from "@/apis/packing";
import { CustomTabPaneContext, CustomTabPaneContextType } from "@/components/TabPane";
import iconFolder from "@/assets/images/fileIcon/folder.svg";
import { SConModal } from "@/styles/SConModal";
import usePackOrderDetail from "../hooks";
import SelectCurrencyVal from "@/components/Selector/SelectCurrencyVal";
import { CellSelectWrap } from "@/components/Selector/style";

interface ItemListProps {
  box: PackBox;
  readonly?: boolean;
};

const ItemList: FC<ItemListProps> = ({ box, readonly = false }) => {
  const { orderData, setOrderData, setSourceItems } = useContext(PackOrderContext);
  const { delItem, loading } = usePackOrderDetail();
  const { onOpen } = useContext<CustomTabPaneContextType<PackBox | BoxItem>>(CustomTabPaneContext);

  const { t } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openItem, setOpenItem] = useState<BoxItem | null>(null);

  const items = useMemo(() => {
    return orderData.boxes?.find(b => b.id === box.id)?.items ?? []
  }, [orderData, box]);

  const handleItemSucc = async (item: BoxItem, type: "ADD" | "EDIT" | "REMOVE") => {
    if (type === "ADD") {
      // setOrderData({
      //   ...orderData,
      //   boxes: (orderData.boxes || []).map(b => {
      //     if (b.id === box.id) {
      //       let items = [...(b.items || [])];
      //       items.push({ ...item });
      //       return { ...b, items };
      //     }
      //     return { ...b };
      //   }),
      // })
      setOpenAdd(false);
    }
    if (type === "EDIT") {
      setOrderData({
        ...orderData,
        boxes: (orderData.boxes || []).map(b => {
          if (b.id === box.id) {
            let items = [...(b.items || [])].map(i => i.id === openItem?.id ? ({ ...item }) : ({ ...i }));
            return { ...b, items };
          }
          return { ...b };
        }),
      });
      setOpenItem(null);
    }
    if (type === "REMOVE") {
      await delItem(item.id);
      setOrderData({
        ...orderData,
        boxes: (orderData.boxes || []).map(b => {
          let items = (b.items || []).filter(i => i.id !== item.id);
          return {
            ...b,
            items
          }
        }),
      });
      setOpenItem(null);
    }
    let newSource = await orderSourceItems(orderData.id);
    setSourceItems([...newSource]);
  };

  const columns: TableProps<BoxItem>['columns'] = [
    {
      title: <SText text={t('inventory.batchNo')} />,
      key: 'lot',
      width: 120,
      render: (_, record) => <SText text={record.lot || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.code')} />,
      key: 'productCode',
      width: 120,
      render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.name')} />,
      key: 'productName',
      width: 150,
      render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('order.brand')} />,
      key: 'model',
      width: 120,
      render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.brand')} />,
      key: 'brand',
      width: 120,
      render: (_, record) => <SText text={record.brand || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.brandType')} />,
      key: 'brandType',
      width: 120,
      render: (_, record) => <SText text={record.brandType || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.exportBrandName')} />,
      key: 'exportBrandName',
      width: 120,
      render: (_, record) => <SText text={record.exportBrandName || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.uom')} />,
      key: 'uom',
      width: 80,
      render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.process')} />,
      key: 'process',
      width: 100,
      render: (_, record) => <SText text={record.product?.process || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.hsCode')} />,
      key: 'hsCode',
      width: 100,
      render: (_, record) => <SText text={record.hsCode || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.originPlace')} />,
      key: 'originPlace',
      width: 100,
      render: (_, record) => <CountrySelect readOnly value={record.originPlace} />
    },
    {
      title: <SText text={t('warehouse.productNameCn')} />,
      key: 'productNameCn',
      width: 120,
      render: (_, record) => <SText text={record.productNameCn || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.productNameEn')} />,
      key: 'productNameEn',
      width: 120,
      render: (_, record) => <SText text={record.productNameEn || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.quantity')} />,
      key: 'boxItemQty',
      width: 100,
      render: (_, record) => <SText text={numFormmater(record.count ?? 0) || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.netWeightG')} />,
      key: 'netWeightG',
      width: 100,
      render: (_, record) => <SText text={numFormmater(record.netWeight ?? 0) || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.declaredUnitPrice')} />,
      key: 'declaredUnitPrice',
      width: 120,
      render: (_, record) => <SText text={numFormmater(record.declaredUnitPrice ?? 0) || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.declaredAmount')} />,
      key: 'declaredAmount',
      width: 120,
      render: (_, record) => <SText text={numFormmater(record.declaredAmount ?? 0) || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.declaredCurrency')} />,
      key: 'declaredCurrency',
      width: 100,
      render: (_, record) => <SText text={record.declaredCurrency} />
    },
    {
      title: <SText text={t('common.attachment')} />,
      width: 120,
      key: "attachment",
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => onOpen("doc", {
              ...item,
              box: { ...box, items: [] }
            })}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: <SText text={t('common.opt')} />,
      width: 100,
      key: 'opt',
      fixed: 'right',
      hidden: box.status !== BoxStatus.DRAFT || readonly,
      render: (_, record) => (
        <Flex gap={2} align='center' >
          <Button type='link' onClick={() => setOpenItem(record)}>{t('requirement.edit')}</Button>
          {box.status === BoxStatus.DRAFT && <SConModal disable={loading === "ItemDel"} title={t('common.tip')}
            content={t('warehouse.delBoxTip', { no: record.product.code })}
            onOk={() => handleItemSucc(record, "REMOVE")}>
            <Button type='text' danger disabled={loading === "ItemDel"}>{t('common.del')}</Button>
          </SConModal>}
        </Flex>
      )
    },
  ];

  const headerChildren = (
    <Flex style={{ flex: 1 }} gap={6}>
      {box.status === BoxStatus.DRAFT && <Button type='dashed' onClick={() => setOpenAdd(true)}>{t('warehouse.boxAddItem')}</Button>}
    </Flex>
  );

  return (
    <Fragment>
      <STableLayout
        headerChildren={!readonly ? headerChildren : null}
        columns={columns as Array<AnyObject>}
        dataSource={items}
        total={items.length}
        extHeight={100}
        pagination={false}
        scrollX={1000}
      />
      {openAdd && <ItemAddOrEdit formType="ADD" item={null} boxId={box.id} open={openAdd} onClose={() => setOpenAdd(false)} onSuccess={data => handleItemSucc(data, "ADD")} />}
      {!!openItem && <ItemAddOrEdit formType="EDIT" item={openItem} boxId={box.id} open={!!openItem} onClose={() => setOpenItem(null)} onSuccess={data => handleItemSucc(data, "EDIT")} />}
    </Fragment>
  )
};

export default ItemList;